import { t } from "@travily/i18n";
import type Badge from "@travily/types/models/BadgesRow";
import type CelebrationsRow from "@travily/types/models/CelebrationsRow";
import type TripsRow from "@travily/types/models/TripsRow";
import type { TripsRowCanTrait } from "@travily/types/models/TripsRow";
import type IndividualsShow from "@travily/types/pages/IndividualsShow";
import type Individual from "@travily/types/pending/models/Individual";
import type { ConfirmDialogState } from "@ui/components/ConfirmDialog";
import { Heading } from "@ui/components/Heading";
import { Celebrations } from "@/components/Celebrations/Celebrations";
import { PassportBadges } from "@/components/PassportBadges/PassportBadges";
import { ProfileFrame } from "@/components/ProfileFrame/ProfileFrame";
import { individualProfileAccount } from "@/components/ProfileFrame/profile-account";
import type { ProfileTabHrefs } from "@/components/ProfileTabs/ProfileTabs";
import { Trips } from "@/components/Trips/Trips";
import { Experience, type ExperienceEntry } from "./components/Experience";

type TripWithCan = TripsRow & TripsRowCanTrait;

export interface ShowViewProps {
  badges?: Badge[];
  can?: IndividualsShow["can"];
  canCreateExperience?: boolean;
  celebrations: CelebrationsRow[];
  deleteState: ConfirmDialogState;
  individual: Individual;
  restricted?: boolean;
  experiences?: ExperienceEntry[];
  /** Profile/store tab hrefs when the profile has a store. */
  tabs?: ProfileTabHrefs;
  trips: TripWithCan[];
  onAddCelebration?: () => void;
  onAcceptFriendship?: () => void;
  onCreateTrip?: () => void;
  onDeclineFriendship?: () => void;
  onDeleteTrip: (sqid: string) => void;
  onEditTrip: (trip: TripWithCan) => void;
  onFriendshipAction?: () => void;
  onShareProfile?: () => void;
  onUnfriend?: () => void;
  onViewAllBadges?: () => void;
  onViewFriends?: () => void;
}

export function ShowView({
  badges = [],
  can,
  canCreateExperience,
  celebrations,
  deleteState,
  individual,
  experiences = [],
  onAcceptFriendship,
  onAddCelebration,
  onCreateTrip,
  onDeclineFriendship,
  onDeleteTrip,
  onEditTrip,
  restricted,
  onFriendshipAction,
  onShareProfile,
  onUnfriend,
  tabs,
  trips,
  onViewAllBadges,
  onViewFriends,
}: ShowViewProps) {
  return (
    <ProfileFrame
      account={individualProfileAccount(individual)}
      currentTab="profile"
      onAcceptFriendship={onAcceptFriendship}
      onDeclineFriendship={onDeclineFriendship}
      onFriendshipAction={onFriendshipAction}
      onShareProfile={onShareProfile}
      onUnfriend={onUnfriend}
      onViewFriends={onViewFriends}
      restricted={restricted}
      tabs={tabs}
    >
      <div className="mt-8 flex w-full flex-col gap-4 lg:mt-12 lg:gap-6">
        <Heading.Root divider="responsive">
          <Heading.Title>{t("passport.badges.title")}</Heading.Title>
        </Heading.Root>
        <PassportBadges badges={badges} totalCount={individual.total_badge_count ?? 0} onViewAll={onViewAllBadges} />
      </div>
      <Experience
        canCreate={canCreateExperience}
        individualDisplayName={individual.display_name}
        experiences={experiences}
      />
      <Celebrations
        celebrations={celebrations}
        onAddCelebration={onAddCelebration}
        ownerDisplayName={individual.display_name}
      />
      <div className="mt-8 w-full lg:mt-12">
        <Trips
          canCreate={can?.create_trip}
          deleteState={deleteState}
          onCreateTrip={onCreateTrip}
          onDeleteTrip={onDeleteTrip}
          onEditTrip={onEditTrip}
          owner={{
            display_name: individual.display_name,
            avatar: individual.avatar,
            initial: individual.initial,
            profile_type: "Individual",
          }}
          trips={trips}
        />
      </div>
    </ProfileFrame>
  );
}
